import usePaginate from "@/components/paginate/usePaginate";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { WorkloadType } from "@/pages/query/types";
import { useQuery } from "@tanstack/react-query";
import {
  getStreamlitNotebookJobs,
  getStreamlitNotebookJobsGraphData,
  getStreamlitNotebookJobsGraphSummary,
} from "@/helpers/apis";
import { Card, Stack, Typography } from "@altimateai/lego";
import { DateRange } from "@/components/dateRange";
import { CommonCostChart } from "@/pages/query/common/charts/CommonCostChart";
import { CommonJobListTable } from "@/pages/query/common/tables/CommonJobListTable";
import { useState, useEffect } from "react";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import SpacesRedirectionButton from "@/components/spaces/SpacesRedirectionButton";
import { SPACES_TAG_MAPPING } from "@/helpers/spacesConstants";
import { Spinner } from "@altimateai/lego";
import { useBreadcrumb } from "@/components/breadcrumbs";

export const NotebooksRoot = () => {
  useBreadcrumb("Notebooks", {
    defaultParents: [{ label: "Code", pathname: "/query" }],
    truncateTo: "/query",
  });
  const { page, setPage, pageSize, updatePageSize } = usePaginate();

  const { startDate, endDate, onUpdate } = useDateFromSearchParams();
  const { searchParams } = useFiltersBySearchParams();
  const sortAttribute = searchParams.get("sortAttribute") ?? "cost";
  const sortOrder = searchParams.get("sortOrder") ?? "desc";
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Query parameters
  const getFilterParams = () => {
    const params: Record<string, string | number | undefined> = {
      page: page + 1,
      size: pageSize,
      sortAttribute,
      sortOrder,
      start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
      end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
      workload_type: WorkloadType.NOTEBOOK,
    };
    return params;
  };

  const { data, isLoading } = useQuery({
    queryKey: ["notebook_jobs", page, pageSize, startDate, endDate, sortAttribute, sortOrder],
    queryFn: () => getStreamlitNotebookJobs(getFilterParams()),
  });

  // Handle successful data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (data) {
      setTotalItems(data?.total ?? 0);
      setTotalPages(data?.pages ?? 0);
    }
  }, [data]);

  const graphParams = {
    start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
    end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
    workload_type: WorkloadType.NOTEBOOK,
  };

  const { data: graphData, isLoading: isGraphLoading } = useQuery({
    queryKey: ["notebook_jobs", "notebooks_graph_data", startDate, endDate],
    queryFn: () => getStreamlitNotebookJobsGraphData(graphParams),
  });

  const graphSummaryQueryKey = ["notebook_jobs", "notebooks_graph_summary", startDate, endDate];

  return (
    <Card className="al-p-4">
      <Stack direction="column">
        <Stack align={"center"} justify={"between"}>
          <Stack direction={"row"} align={"center"} gap={"md"}>
            <Typography variant="page-heading">Notebook Jobs</Typography>
            <SpacesRedirectionButton tag={SPACES_TAG_MAPPING.WORKLOADS} />
          </Stack>
          <DateRange
            startDate={startDate}
            endDate={endDate}
            availableStaticRanges={["Last day", "Last 7 days", "Last 28 days"]}
            onDateRangeSelect={onUpdate}
          />
        </Stack>
        <Stack direction="column">
          {isGraphLoading ? (
            <Stack
              align={"center"}
              justify={"center"}
              className={"al-w-full al-border al-rounded-md al-min-h-32"}
            >
              <Spinner />
              Loading graph...
            </Stack>
          ) : graphData?.data && graphData.data.length > 0 ? (
            <CommonCostChart
              graphData={graphData.data ?? []}
              graphSummaryQueryKey={graphSummaryQueryKey}
              graphSummaryQueryFn={getStreamlitNotebookJobsGraphSummary}
              graphSummaryParams={graphParams}
            />
          ) : (
            <div className="al-w-full al-text-center">No data is present</div>
          )}
        </Stack>
        <Stack direction={"column"} gap={"lg"} className={"al-mt-4"}>
          <Typography variant="page-heading">Job list</Typography>
          <CommonJobListTable
            isLoading={isLoading}
            basePath={"notebooks"}
            tableData={data}
            page={page}
            pageSize={pageSize}
            totalItems={totalItems}
            totalPages={totalPages}
            setPage={setPage}
            updatePageSize={updatePageSize}
          />
        </Stack>
      </Stack>
    </Card>
  );
};
